import { Button, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { DialogActionTrigger, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogRoot, DialogTitle, DialogTrigger } from '@src/components/chakra-ui/dialog';
import { usePitchDeckUpload } from '@src/components/sco/PitchDeckAuditor/hooks/usePitchDeckUpload';
import { UploadDocumentButton } from '@src/components/sco/UploadDocumentButton';
import { scoBackendUrl } from '@src/lib/startups-url';
import { useState } from 'react';
import { LuTriangleAlert, LuUpload } from 'react-icons/lu';

/**
 * "Upload a new version" — swaps the deck's PDF for a freshly-uploaded one and re-runs the
 * full audit on the *same* PitchDeck record. On success the deck is reset to its processing
 * state, so the workspace flips back to the live processing view; `onReplaced` refreshes it.
 */
export function UploadNewVersionDialog({
  pitchDeckId,
  onReplaced
}: {
  pitchDeckId: string;
  onReplaced?: () => void;
}) {
  const {
    replaceDeckFile,
    replacing
  } = usePitchDeckUpload();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const busy = replacing || working;
  const handleUploaded = async (_url: string, storedFileId?: string) => {
    setError(null);
    if (!storedFileId) {
      setError('Upload succeeded but no file id was returned. Please try again.');
      return;
    }
    setWorking(true);
    try {
      const id = await replaceDeckFile(pitchDeckId, storedFileId, {
        analyze: true
      });
      if (id) {
        setOpen(false);
        onReplaced?.();
      } else {
        setError('We could not start the audit. Please try again.');
      }
    } catch (e: any) {
      setError(e?.message || 'Something went wrong starting the audit.');
    } finally {
      setWorking(false);
    }
  };
  return <DialogRoot open={open} onOpenChange={e_0 => {
    if (busy) {
      return;
    } // don't let the dialog close mid-upload
    setOpen(e_0.open);
    if (!e_0.open) {
      setError(null);
    }
  }}>
      <DialogTrigger asChild>
        <Button size={'md'} variant={'outline'}>
          <LuUpload />
          Upload a new version
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload a new version</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <VStack align={'stretch'} gap={3}>
            <Text color={'fg.muted'} fontSize={'sm'}>
              Pick an updated PDF of your deck. It replaces the current file and we re-run the full audit on
              the new version — your existing scores and rewrites will be replaced.
            </Text>
            <Text color={'fg.subtle'} fontSize={'xs'}>
              PDF up to 40&nbsp;MB. (Export PowerPoint / Keynote to PDF first.)
            </Text>
            {error && <HStack color={'red.fg'} fontSize={'sm'} gap={2}>
                <Icon as={LuTriangleAlert} boxSize={4} />
                <Text>{error}</Text>
              </HStack>}
          </VStack>
        </DialogBody>
        <DialogFooter>
          <DialogActionTrigger asChild>
            <Button disabled={busy} size={'sm'} variant={'outline'}>
              Cancel
            </Button>
          </DialogActionTrigger>
          <UploadDocumentButton allowedExtensions={['.pdf']} colorPalette={'orange'} loading={busy} loadingText={'Starting…'} maxSizeInBytes={50 * 1024 * 1024} size={'sm'} storedFile={{
          attachmentType: 'PitchDeck',
          contextKey: 'original'
        }} uploadUrl={scoBackendUrl('/assets/upload-pitch-deck')} onError={(e_1: any) => setError(e_1?.message || 'Upload failed. Please try again.')} onSuccess={handleUploaded}>
            <LuUpload />
            Choose a PDF
          </UploadDocumentButton>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>;
}